//Date edit control
//concept of implementing ngModel came from https://embed.plnkr.co/nqKUSPWb6w5QXr8a0wEu/?show=preview
//Uses moment.js for date parsing/conversion  https://momentjs.com/docs/#/parsing/
//TODO implement property to specify display mask
//TODO implement property to specify data format mask

import {
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    HostListener,
    Input,
    OnDestroy,
    OnInit,
    Output,
    Renderer2,
    ViewChild
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { NgbDateStruct, NgbTimeStruct } from '@ng-bootstrap/ng-bootstrap';
import * as moment from 'moment';

const noop = () => { };

/**
 * Sure MGA Date Input Component with dropdown calendar.
 *
 * @example
 * <ep-date-input [(ngModel)]="model.Criteria.Eff_From" name="Eff_From"></ep-date-input>
 * @example
 * <ep-date-input
 *             [(ngModel)]="model.Criteria.Eff_To"
 *             (ngModelChange)="applyModelRules()"
 *             name="Eff_To" [minDate]="model.Criteria.Eff_From"
 *             [popupOnFocus]="false"
 *             [disabled]="model.Criteria.Range !== 'EFF'">
 *  </ep-date-input>
 *
 * @example
 * To show timepicker when ep-date-input is standalone (outside of ep-field):
 * <ep-date-input [(ngModel)]="model.Criteria.Eff_From" name="Eff_From" [format]="'datetime'"></ep-date-input>
 *
 *
 * @example
 * To show timepicker when using ep-field:
 * <ep-field columnSpan="2" label="Effective"
 *            controlPath="Contract.Eff_Date"
 *            format="datetime">
 * </ep-field>
 *
 */
@Component({
    standalone: false,
    selector: 'ep-date-input',
    template: `
    <div [class]="'unity-form-group date'" style="margin:0px; padding:0px">
      <label *ngIf="!noLabel"><ng-content></ng-content></label>
      <div #dpGroup class="unity-form-row" style="position:relative">
        <input
          class="unity-date"
          #inputElement
          mask="{{ useTime ? ngxDateTimeMask : ngxDateMask }}"
          [keepCharacterPositions]="keepCharacterPositions"
          [leadZeroDateTime]="leadZeroDateTime"
          [(ngModel)]="currentInput"
          (ngModelChange)="inputChanged($event)"
          [disabled]="disabled"
          (keyup.f4)="onKeyF4()"
          (focus)="onFocus()"
          (blur)="onBlur()"
          size="15"
        />
        <svg
          #toggleButton
          class="unity-icon unity-icon-calendar"
          (click)="disabled ? null : toggleCalendar()"
          tabindex="-1"
          [style.cursor]="disabled ? 'not-allowed' : ''"
        >
          <use xlink:href="#unity-icon-calendar"></use>
        </svg>
        <ng-container *ngIf="useTime; else dateOnly">
          <ngb-datepicker
            *ngIf="isCalendarOpen"
            [(ngModel)]="currentDate"
            [startDate]="currentDate"
            [minDate]="_minDate"
            [maxDate]="_maxDate"
            firstDayOfWeek="7"
            style="position:absolute;z-index:3;background-color:#ffffff;top: var(--height-input);"
            (click)="$event.preventDefault(); $event.stopPropagation()"
            (dateSelect)="datePickerChanged($event)"
            [footerTemplate]="footerTemplate"
            (navigate)="datePickerNavigate($event)"
          >
            <ng-template #footerTemplate>
              <ngb-timepicker
                [hourStep]="hourStep"
                [minuteStep]="minuteStep"
                [meridian]="meridian"
                [readonlyInputs]="disabled"
                tabindex="-1"
                [(ngModel)]="currentTime"
                (ngModelChange)="timePickerChanged($event)"
              ></ngb-timepicker>
            </ng-template>
          </ngb-datepicker>
        </ng-container>
        <ng-template #dateOnly>
          <ngb-datepicker
            *ngIf="isCalendarOpen"
            [(ngModel)]="currentDate"
            [startDate]="currentDate"
            [minDate]="_minDate"
            [maxDate]="_maxDate"
            firstDayOfWeek="7"
            style="position:absolute;z-index:3;background-color:#ffffff;top: var(--height-input);"
            (click)="$event.preventDefault(); $event.stopPropagation()"
            (dateSelect)="datePickerChanged($event)"
            (navigate)="datePickerNavigate($event)"
          >
          </ngb-datepicker>
        </ng-template>
      </div>
    </div>
  `,
    providers: [{ provide: NG_VALUE_ACCESSOR, useExisting: DateInputComponent, multi: true }]
})
export class DateInputComponent implements ControlValueAccessor, AfterViewInit, OnDestroy, OnInit {
    //unity-design condition
    @Input() noLabel: boolean = false;

    // Inputs for timepicker control
    @Input() meridian: boolean = true;
    @Input() hourStep = 1;
    @Input() minuteStep = 1;

    @ViewChild('dpGroup', { static: false })
    dpGroup: ElementRef;

    @ViewChild('toggleButton', { static: false })
    toggleButton: ElementRef;

    @ViewChild('inputElement', { static: false })
    inputElement: ElementRef;

    public currentDate: NgbDateStruct;

    public currentTime: NgbTimeStruct;

    public currentInput: string;

    private innerValue: any = '';

    public _minDate: NgbDateStruct;
    public _maxDate: NgbDateStruct;

    private displayFormat = '';
    private dataFormat = 'YYYY-MM-DDTHH:mm:ss-HH:mm';

    // ngx-mask properties
    public ngxDateMask = 'M0/d0/0000';
    public ngxDateTimeMask = 'M0/d0/0000 Hh:m0 UU';
    public keepCharacterPositions = true;
    public leadZeroDateTime = true;

    public timeMask = `Hh:m0 UU`;
    public specialCharacters = [':'];
    public dropSpecialCharacters = false;

    //Placeholders for the callbacks which are later provided
    //by the Control Value Accessor
    private onTouchedCallback: () => void = noop;
    private onChangeCallback: (_: any) => void = noop;

    /**
     * Input to control whether calendar automatically displays when date input gets focus. Default is true.
     */
    @Input()
    popupOnFocus: boolean = true;

    /**
     * Input to indicate whether input should be disabled.  Default is false.
     */
    @Input()
    disabled: boolean = false;

    /**
     * Input to set the minimum date that can be selected.   Default is no mininimum date.
     */
    @Input()
    set minDate(value: string) {
        let m = moment.parseZone(value);
        if (m && m.isValid()) {
            this._minDate = { year: m.year(), month: m.month() + 1, day: m.date() };
        } else {
            this._minDate = { year: 1900, month: 1, day: 1 };
        }
    }

    /**
     * Input to set the maximum date that can be selected.   Default is no maximum date.
     */
    @Input()
    set maxDate(value: string) {
        let m = moment.parseZone(value);
        if (m && m.isValid()) {
            this._maxDate = { year: m.year(), month: m.month() + 1, day: m.date() };
        } else {
            this._maxDate = { year: 2099, month: 12, day: 31 };
        }
    }

    @Input() format: 'date' | 'datetime' = 'date';

    get useTime(): boolean {
        return this.format === 'datetime';
    }

    @Output() blur = new EventEmitter();

    @Output() focus = new EventEmitter();

    @Output() calendarClosed = new EventEmitter();

    isCalendarOpen = false;
    unlistenCallback: any;
    isInitialized = false;
    backspaceKeyPressed = false;

    constructor(private renderer: Renderer2, private cdRef: ChangeDetectorRef) { }

    @HostListener('keydown', ['$event'])
    handleKeydown(event: any) {
        if (this.isEscapeKey(event)) {
            if (this.isCalendarOpen) {
                this.isCalendarOpen = false;
                this.calendarClosed.emit();
            }
            event.preventDefault();
            event.stopPropagation();
        } else if (this.isTabOrShiftTabKey(event)) {
            this.isCalendarOpen = false;
        }
    }
    isEscapeKey(event: KeyboardEvent): boolean {
        return event.key == 'Escape' || event.key == 'Esc';
    }

    isTabOrShiftTabKey(event: KeyboardEvent): boolean {
        return event.key == 'Tab' || (event.shiftKey && event.key == 'Tab');
    }

    isBackspaceKey(event: KeyboardEvent): boolean {
        return event.key == 'Backspace';
    }

    isDeleteKey(event: KeyboardEvent): boolean {
        return event.key == 'Delete';
    }

    ngOnInit() {
        this.displayFormat = this.useTime ? 'MM/DD/YYYY hh:mm A' : 'MM/DD/YYYY';
    }

    ngAfterViewInit() {
        this.unlistenCallback = this.renderer.listen('window', 'click', (e: Event) => {
            if (!this.toggleButton.nativeElement.contains(e.target) && !this.dpGroup.nativeElement.contains(e.target)) {
                if (this.isCalendarOpen) {
                    this.isCalendarOpen = false;
                    this.calendarClosed.emit();
                }
                this.cdRef.markForCheck();
            }
        });

        this.isInitialized = true;
    }

    ngOnDestroy() {
        this.unlistenCallback();
    }

    public inputChanged(inputVal: string) { }

    public datePickerChanged(dateVal: NgbDateStruct) {
        let m;
        if (this.useTime) {
            if (!this.currentTime) {
                this.currentTime = {
                    hour: 0,
                    minute: 0,
                    second: 0
                };
            }
            m = moment.parseZone({
                year: dateVal.year,
                month: dateVal.month - 1,
                day: dateVal.day,
                hour: this.currentTime.hour,
                minute: this.currentTime.minute,
                second: this.currentTime.second
            });
        } else {
            m = moment.parseZone({ year: dateVal.year, month: dateVal.month - 1, day: dateVal.day });
        }

        if (m && m.isValid()) {
            this.value = m.format(this.dataFormat);
            this.currentInput = m.format(this.displayFormat);
        } else {
            this.value = '';
            this.currentDate = null;
            this.currentTime = null;
        }

        if (!this.useTime) {
            if (this.isCalendarOpen) {
                this.isCalendarOpen = false;
            }
            this.onTouchedCallback();
            this.calendarClosed.emit();
        } else {
            this.onTouchedCallback();
            this.blur.emit();
        }
    }

    public timePickerChanged(timeVal: NgbTimeStruct) {
        let newDate: moment.Moment;

        if (!this.currentDate) {
            newDate = moment();
            this.currentDate = {
                year: newDate.year(),
                month: newDate.month() + 1,
                day: newDate.date()
            };
        }

        let currTime: NgbTimeStruct = {
            hour: timeVal ? timeVal.hour : 0,
            minute: timeVal ? timeVal.minute : 0,
            second: 0
        };

        let m = moment.parseZone({
            year: this.currentDate.year,
            month: this.currentDate.month - 1,
            day: this.currentDate.day,
            hour: currTime.hour,
            minute: currTime.minute,
            second: currTime.second
        });
        if (m && m.isValid()) {
            this.value = m.format(this.dataFormat);
            this.currentInput = m.format(this.displayFormat);
        } else {
            this.value = '';
            this.currentDate = null;
            this.currentTime = null;
        }

        this.onTouchedCallback();
        this.blur.emit();
    }

    public datePickerNavigate(event: any) {
        if (!this.isInitialized) {
            return;
        }

        let nextDate = event && event.next ? event.next : null;
        let current = event && event.current ? event.current : null;

        if (!current) {
            return;
        }

        // if (this.backspaceKeyPressed) {
        //   return;
        // }

        setTimeout(() => {
            let m;
            let dayVal;
            let currDayVal;

            if (this.currentInput) {
                currDayVal = this.currentInput.split('/')[1];
            } else {
                let today = new Date().getDate();
                currDayVal = today.toString();
            }

            dayVal = currDayVal ? currDayVal : '01';

            if (this.useTime) {
                if (!this.currentTime) {
                    this.currentTime = {
                        hour: 0,
                        minute: 0,
                        second: 0
                    };
                }
                m = moment.parseZone({
                    year: nextDate.year,
                    month: nextDate.month - 1,
                    day: dayVal,
                    hour: this.currentTime.hour,
                    minute: this.currentTime.minute,
                    second: this.currentTime.second
                });
            } else {
                m = moment.parseZone({ year: nextDate.year, month: nextDate.month - 1, day: dayVal });
            }

            if (m && m.isValid()) {
                this.value = m.format(this.dataFormat);
                this.currentInput = m.format(this.displayFormat);
            }
        });
    }

    hasTimeInfo(value: string): boolean {
        return value.indexOf('T') > -1;
    }

    ////get accessor
    private get value(): any {
        return this.innerValue;
    }

    //set accessor including call the onchange callback
    private set value(v: any) {
        if (v !== this.innerValue) {
            let newValue = '';
            if (v && typeof v === 'string' && this.hasTimeInfo(v)) {
                let lastDash = v.lastIndexOf('-');
                if (lastDash !== -1 && lastDash > 1) {
                    newValue = v.slice(0, lastDash) + '-00:00';
                    this.innerValue = newValue;
                    this.onChangeCallback(newValue);
                }
            } else {
                this.innerValue = v;
                this.onChangeCallback(v);
            }
        }
    }

    public onBlur() {
        let m = moment.parseZone(this.currentInput, this.displayFormat);
        let minValid: boolean = true;
        let maxValid: boolean = true;

        if (this._minDate) {
            let mMin = moment.parseZone(
                this._minDate.month + '/' + this._minDate.day + '/' + this._minDate.year,
                this.displayFormat
            );
            minValid = m.isBefore(mMin) ? false : true;
        }
        if (this._maxDate) {
            let mMax = moment.parseZone(
                this._maxDate.month + '/' + this._maxDate.day + '/' + this._maxDate.year,
                this.displayFormat
            );
            maxValid = m.isAfter(mMax) ? false : true;
        }

        if (m && m.isValid() && minValid && maxValid) {
            this.value = m.format(this.dataFormat);
            this.currentDate = { year: m.year(), month: m.month() + 1, day: m.date() };
            this.currentInput = m.format(this.displayFormat);
            if (this.useTime) {
                this.currentTime = { hour: m.hour(), minute: m.minute(), second: 0 };
            }
        } else {
            this.value = '';
            this.currentDate = null;
            this.currentTime = null;
            this.currentInput = '';
        }

        this.blur.emit();
        this.onTouchedCallback();
    }

    public onKeyF4() {
        this.toggleCalendar();
    }

    public onFocus() {
        if (this.popupOnFocus && !this.isCalendarOpen) {
            this.isCalendarOpen = true;
            this.focus.emit();
        }
        // if (this.backspaceKeyPressed) {
        //   this.backspaceKeyPressed = false;
        // }
    }

    public onCalendarClick() { }

    toggleCalendar() {
        this.isCalendarOpen = !this.isCalendarOpen;
    }

    //From ControlValueAccessor interface
    writeValue(value: any) {
        if (value !== this.innerValue) {
            this.innerValue = value;
            let m = moment.parseZone(value);
            if (value && value > '' && m && m.isValid()) {
                this.currentInput = m.format(this.displayFormat);
                this.currentDate = { year: m.year(), month: m.month() + 1, day: m.date() };
                if (this.useTime) {
                    let timeVal = {
                        hour: m.hour() ? m.hour() : 0,
                        minute: m.minute() ? m.minute() : 0,
                        second: 0
                    };
                    this.currentTime = { hour: timeVal.hour, minute: timeVal.minute, second: timeVal.second };
                }
            } else {
                this.currentDate = null;
                this.currentInput = null;
                this.currentTime = null;
            }
            this.cdRef.detectChanges();
        }
    }

    //From ControlValueAccessor interface
    registerOnChange(fn: any) {
        this.onChangeCallback = fn;
    }

    //From ControlValueAccessor interface
    registerOnTouched(fn: any) {
        this.onTouchedCallback = fn;
    }

    //From ControlValueAccessor interface -  Allows Angular to disable the input.
    setDisabledState(isDisabled: boolean): void {
        this.disabled = isDisabled;
    }
}
